import { Dispatch, RefObject, SetStateAction, useMemo, useRef, useState } from "react";

import {
  deleteDiscussionMessage,
  deleteDiscussionThread,
  updateDiscussionMessage,
  updateDiscussionThread,
} from "../../../../../service/interaction";
import { ServiceError, Status } from "../../../../../service/Shared";
import { useAuth } from "../../../../../useAuth";
import { convertDateToStringDDMMYYYY, isSameDay, useIsLoadingWrapper } from "../../../../../utils";
import { Toast } from "../../../../general";
import { ProfileMenuItemType } from "../../../../navigation";
import { Message, Thread } from "../../../models";

interface useDiscussionMessageProps {
  threads: Thread[];
  setThreads: Dispatch<SetStateAction<Thread[]>>;
  message: Message;
  closeDiscussion: () => void;
  threadUuid: string;
  internalTeamSelected: boolean;
}

interface useDiscussionMessageReturnData {
  showResolve: boolean;
  showMisc: boolean;
  editable: boolean;
  editedMessage: string;
  editableErrors: ServiceError[] | undefined;
  setEditableErrors: Dispatch<SetStateAction<ServiceError[] | undefined>>;
  resolveMenuItem: ProfileMenuItemType;
  otherMessagesMenuItems: ProfileMenuItemType[] | undefined;
  setShowResolve: Dispatch<SetStateAction<boolean>>;
  setShowMisc: Dispatch<SetStateAction<boolean>>;
  setEditable: Dispatch<SetStateAction<boolean>>;
  setEditedMessage: Dispatch<SetStateAction<string>>;
  formatDate: (date: Date) => string;
  handleEditMessage: (messageUuid: string) => Promise<void>;
  resolveRef: RefObject<HTMLDivElement>;
  miscRef: RefObject<HTMLDivElement>;
  firstMessageMenuItems: ProfileMenuItemType[] | undefined;
  showDeleteDiscussionModal: boolean;
  confirmDelete: () => Promise<void>;
  cancelDelete: () => void;
  showIsUnread: boolean;
  isHandleEditMessageLoading: boolean;
}

export const useDiscussionMessage = ({
  threads,
  setThreads,
  message,
  closeDiscussion,
  threadUuid,
}: useDiscussionMessageProps): useDiscussionMessageReturnData => {
  const { user } = useAuth();
  const userUuid = useMemo(() => user?.userUuid ?? "", [user]);

  const [showResolve, setShowResolve] = useState<boolean>(false);
  const [showMisc, setShowMisc] = useState<boolean>(false);

  const [editable, setEditable] = useState<boolean>(false);
  const [editedMessage, setEditedMessage] = useState<string>(message.content);
  const [editableErrors, setEditableErrors] = useState<ServiceError[] | undefined>();

  const [showDeleteDiscussionModal, setShowDeleteDiscussionModal] = useState<boolean>(false);
  const [isHandleEditMessageLoading, setIsHandleEditMessageLoading] = useState(false);

  const currentThread = threads.find((t) => t.uuid === threadUuid);
  const lastMessageInThread = currentThread?.messages.at(-1);
  const showIsUnread =
    !!lastMessageInThread &&
    lastMessageInThread.createdByUserUuid !== userUuid &&
    message.uuid === lastMessageInThread.uuid;

  const messageIndex = currentThread?.messages.findIndex((m) => m.uuid === message.uuid) ?? -1;
  const noOtherUserMessagesAfter =
    messageIndex === -1 ||
    (currentThread?.messages.slice(messageIndex + 1).every((m) => m.createdByUserUuid === userUuid) ?? true);
  const canEditOrDelete = message.createdByUserUuid === userUuid && noOtherUserMessagesAfter;

  const firstMessageMenuItems: ProfileMenuItemType[] | undefined = canEditOrDelete
    ? [
        { id: 1, value: "Edit message", action: () => setEditable(true) },
        { id: 2, value: "Delete thread", action: () => setShowDeleteDiscussionModal(true) },
      ]
    : undefined;

  const otherMessagesMenuItems: ProfileMenuItemType[] | undefined = canEditOrDelete
    ? [
        { id: 1, value: "Edit message", action: () => setEditable(true) },
        {
          id: 2,
          value: "Delete message",
          action: async () => {
            const res = await deleteDiscussionMessage({ messageUuid: message.uuid });
            if (res.status === Status.Success) {
              setThreads(
                threads.map((t) => {
                  if (t.uuid === threadUuid) {
                    return { ...t, messages: t.messages.filter((m) => m.uuid !== message.uuid) };
                  }
                  return t;
                })
              );
              Toast.success({ message: "Message deleted" });
            }
            if (res.status === Status.Error && res.errors && res.errors.length) {
              Toast.error({ message: res.errors[0].message });
            }
          },
        },
      ]
    : undefined;

  const resolveMenuItem = {
    id: 1,
    value: "Resolve discussion",
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    action: async (args: { [p: string]: any } | undefined): Promise<void> => {
      const res = await updateDiscussionThread({ isResolved: true, rowVersion: 1, threadUuid: args?.threadUuid });

      if (res.status === Status.Success) {
        setThreads(
          threads.map((t) => {
            if (t.uuid === args?.threadUuid) {
              return {
                ...t,
                resolved: true,
              };
            }

            return t;
          })
        );

        Toast.success({ message: "Discussion resolved" });
      }

      if (res.status === Status.Error && res.errors && res.errors.length) {
        Toast.error({ message: res.errors[0].message });
      }

      setShowResolve(false);
    },
  };

  const resolveRef = useRef<HTMLDivElement>(null);
  const miscRef = useRef<HTMLDivElement>(null);

  const handleEditMessage = useIsLoadingWrapper(async (messageUuid: string): Promise<void> => {
    const res = await updateDiscussionMessage({
      acceptOrReject: null,
      rejectedReason: null,
      content: editedMessage,
      messageUuid,
      rowVersion: message?.rowVersion ?? 0,
    });

    if (res.status === Status.Success) {
      setThreads(
        threads.map((t) => {
          if (t.messages.find((m) => m.uuid === messageUuid)) {
            return {
              ...t,
              messages: t.messages.map((m) => {
                if (m.uuid === messageUuid) {
                  return {
                    ...m,
                    content: editedMessage,
                  };
                }
                return m;
              }),
            };
          }

          return t;
        })
      );

      setEditable(false);
      setEditableErrors(undefined);
      Toast.success({ message: "Message updated" });
    } else if (res.status === Status.Error && res.errors) {
      setEditableErrors(res.errors);
    }
  }, setIsHandleEditMessageLoading);

  const formatDate = (date: Date): string => {
    const currentDate = new Date();
    const yesterdayDate = new Date(currentDate);
    yesterdayDate.setDate(yesterdayDate.getDate() - 1);

    if (isSameDay(currentDate, date)) {
      return "Today";
    }
    if (isSameDay(yesterdayDate, date)) {
      return "Yesterday";
    }
    return convertDateToStringDDMMYYYY(date);
  };

  const confirmDelete = async (): Promise<void> => {
    setShowDeleteDiscussionModal(false);
    const res = await deleteDiscussionThread({ threadUuid });

    if (res.status === Status.Success) {
      setThreads(threads.filter((t) => t.uuid !== threadUuid));
      closeDiscussion();
      Toast.success({ message: "Discussion deleted" });
    }

    if (res.status === Status.Error && res.errors && res.errors.length) {
      Toast.error({ message: res.errors[0].message });
    }
  };

  const cancelDelete = (): void => {
    setShowDeleteDiscussionModal(false);
  };

  return {
    showResolve,
    showMisc,
    editable,
    editedMessage,
    editableErrors,
    setEditableErrors,
    resolveMenuItem,
    otherMessagesMenuItems,
    setShowResolve,
    setShowMisc,
    setEditable,
    setEditedMessage,
    formatDate,
    handleEditMessage,
    resolveRef,
    miscRef,
    firstMessageMenuItems,
    showDeleteDiscussionModal,
    confirmDelete,
    cancelDelete,
    showIsUnread,
    isHandleEditMessageLoading,
  };
};
